/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AnyAction, Dispatch } from "redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { AxiosAction } from "order-manager/types/api/common.type"
import { sleep } from "utils/misc"
import {
  getResponseErrorEventReason,
  getResponseErrorEventReasonForRTK,
  TroyWebTracker,
} from "utils/tracking"
import { FetchMenuGroupLinkedStoresResponse } from "types/api/menuFetchMenuGroupLinkedStores.type"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import { actions } from "store/duck/menu"
import { useLazyFetchMenuGroupLinkedStoresQuery } from "queries/menuApi"
import { EventCategoryEng, EventActionEng } from "constants/tracking"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"

export interface Labels {
  LOAD_TIME: number
  LOADING_STATUS: string
  REASON: string
  ERROR_MSG: string
}

type FetchMenuGroupLinkedStoresTrigger = ReturnType<
  typeof useLazyFetchMenuGroupLinkedStoresQuery
>[0]

export const fetchAllMenuGroupLinkedStoreListFactory = (
  dispatch: Dispatch,
  menuGroupID: string,
  merchantGroupID: string,
  isRtkFetchMenuGroupLinkedStoresEnabled: boolean,
  isRtkFlagLoading: boolean,
  fetchMenuGroupLinkedStoresRtk: FetchMenuGroupLinkedStoresTrigger,
) => async () => {
  let page = 1
  let hasMore = false
  const pageSize = 100
  const errors = []
  let storeIDs: string[] = []
  const sendTracking = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchMenuGroupLinkedStores,
      labels: {
        MERCHANT_ID: merchantGroupID,
        IS_RTK_ENABLED: isRtkFetchMenuGroupLinkedStoresEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "fetchMenuGroupLinkedStores",
      },
    })
  }

  // While migration flags are loading, do not fall back to old Redux: the enabled bit is
  // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
  // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
  // the trigger UI while loading so clicks are not silently dropped.
  if (isRtkFlagLoading) {
    return
  }

  dispatch(actionCreator.global.showLoading())

  if (isRtkFetchMenuGroupLinkedStoresEnabled) {
    // Reset Redux state before first page (mirrors FETCH_MENU_GROUP_LINKED_STORES pending reducer)
    dispatch({
      type: actions.FETCH_MENU_GROUP_LINKED_STORES,
      payload: { append: false },
    })

    do {
      const previousAction = {
        ...actionCreator.menu.fetchMenuGroupLinkedStores({
          menuGroupID,
          merchantGroupID,
          page,
          pageSize,
          append: page > 1,
        }),
        // axiosBaseQuery already owns the RTK failure notification and monitoring.
        showError: false,
        notTrackError: true,
      }
      // eslint-disable-next-line no-await-in-loop
      const result = await fetchMenuGroupLinkedStoresRtk({
        menuGroupID,
        merchantGroupID,
        page,
        pageSize,
      })

      if ("error" in result) {
        const error = result.error as {
          data?: unknown
          headers?: Record<string, string>
          message?: string
          status?: number
        }
        errors.push(getResponseErrorEventReasonForRTK(result))
        // Dispatch fail for backward compat — state.menu.menuGroupLinkedStores is read by other components
        dispatch({
          type: getActionFail(actions.FETCH_MENU_GROUP_LINKED_STORES),
          payload: {
            data: (result.error as any)?.data,
            status: (result.error as any)?.status,
            config: {
              method: previousAction.payload.request.method,
              url: previousAction.payload.request.url,
            },
          },
          meta: {
            previousAction,
          },
          error: {
            message: error.message,
            response: {
              status: error.status,
              data: error.data,
              headers: error.headers,
            },
          },
        })
        hasMore = false
      } else if (result.data) {
        const responseData = result.data
        storeIDs = [...storeIDs, ...(responseData.storeIDs ?? [])]

        // Dispatch success for backward compat — state.menu.menuGroupLinkedStores is read by other components
        dispatch({
          type: getActionSuccess(actions.FETCH_MENU_GROUP_LINKED_STORES),
          payload: { data: responseData },
          meta: {
            previousAction,
          },
        })

        hasMore = responseData.hasMore ?? false
      } else {
        // Match the legacy reducer's empty-response behavior: initialize its
        // compatibility state with an empty store list rather than leaving it undefined.
        dispatch({
          type: getActionSuccess(actions.FETCH_MENU_GROUP_LINKED_STORES),
          payload: { data: { storeIDs: [], hasMore: false } },
          meta: {
            previousAction,
          },
        })
        hasMore = false
      }

      if (!hasMore) break
      page += 1
      // eslint-disable-next-line no-await-in-loop
      await sleep(1000)
    } while (hasMore)

    sendTracking(true)
  } else {
    do {
      // eslint-disable-next-line no-await-in-loop
      const resp = (await dispatch(
        actionCreator.menu.fetchMenuGroupLinkedStores({
          menuGroupID,
          merchantGroupID,
          page,
          pageSize,
          append: hasMore,
        }) as AnyAction,
      )) as AxiosAction<FetchMenuGroupLinkedStoresResponse>

      if (resp?.type.endsWith("_FAIL")) {
        const errorEventReason = getResponseErrorEventReason(resp)
        errors.push(errorEventReason)
      } else if ((resp?.payload as any)?.status === 200) {
        storeIDs = [...storeIDs, ...(resp?.payload?.data?.storeIDs ?? [])]
      }

      page += 1
      hasMore = resp?.payload?.data?.hasMore ?? false
      if (!hasMore) {
        break
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(1000)
    } while (hasMore)

    sendTracking(false)
  }
}

function useFetchAllMenuGroupLinkedStoreList() {
  const dispatch = useDispatch()
  const {
    menu: {
      menuGroupDetail: { menuGroupID },
      merchantGroupID,
    },
  } = useSelector((s: AppState) => s)
  const {
    flags: {
      fetchMenuGroupLinkedStores: isRtkFetchMenuGroupLinkedStoresEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [
    fetchMenuGroupLinkedStoresRtk,
  ] = useLazyFetchMenuGroupLinkedStoresQuery()

  const fetchAllMenuGroupLinkedStoreList = useMemo(
    () =>
      fetchAllMenuGroupLinkedStoreListFactory(
        dispatch,
        menuGroupID,
        merchantGroupID,
        isRtkFetchMenuGroupLinkedStoresEnabled,
        isRtkFlagLoading,
        fetchMenuGroupLinkedStoresRtk,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      menuGroupID,
      merchantGroupID,
      isRtkFetchMenuGroupLinkedStoresEnabled,
      isRtkFlagLoading,
    ],
  )
  return fetchAllMenuGroupLinkedStoreList
}

export default useFetchAllMenuGroupLinkedStoreList
